/**
 * Les documents d'un chantier : plans, cahiers des charges, autres pièces
 * (`20261029120000_project_documents`).
 *
 * Déposés par le patron ou le conducteur ; vus d'office par l'encadrement
 * (patron, conducteur, assistant, chef de chantier sur ses chantiers) ; vus
 * de l'ouvrier seulement s'ils lui sont partagés, sur ses chantiers. La base
 * le tient, sur la table comme sur le fichier — ce module ne fait que dire
 * à l'écran ce qu'il est permis de proposer.
 */
import type { Role } from "../../context.js";

export const SITE_DOCUMENT_KINDS = ["plan", "cahier_des_charges", "autre"] as const;
export type SiteDocumentKind = (typeof SITE_DOCUMENT_KINDS)[number];

export const SITE_DOCUMENT_KIND_LABELS: Record<SiteDocumentKind, string> = {
  plan: "Plan",
  cahier_des_charges: "Cahier des charges",
  autre: "Autre document",
};

export interface SiteDocument {
  id: string;
  projectId: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  kind: SiteDocumentKind;
  /** Partagé au terrain : l'ouvrier affecté au chantier le voit. */
  sharedWithField: boolean;
  createdAt: string;
}

/** 25 Mo : la limite du bucket. Un plan PDF en A0 tient largement dedans. */
export const SITE_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

/**
 * Les types que le bucket accepte. Ni DWG ni archive : un navigateur de
 * téléphone ne les ouvre pas, et c'est sur un téléphone qu'on les lit.
 */
export const SITE_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;

/** Ce que l'écran annonce à côté du bouton « Déposer ». */
export const SITE_DOCUMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx";

/** Pourquoi un fichier ne peut pas être déposé, ou `null` s'il le peut. */
export function siteDocumentProblem(file: { name: string; type: string; size: number }): string | null {
  if (file.size <= 0) return "Ce fichier est vide.";
  if (file.size > SITE_DOCUMENT_MAX_BYTES) {
    return `Ce fichier dépasse 25 Mo (${(file.size / 1024 / 1024).toFixed(1).replace(".", ",")} Mo).`;
  }
  if (!(SITE_DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type)) {
    return "Ce type de fichier n'est pas accepté : PDF, image, Word ou Excel.";
  }
  if (file.name.trim().length === 0 || file.name.length > 200) {
    return "Le nom du fichier doit faire entre 1 et 200 caractères.";
  }
  return null;
}

/**
 * `<entreprise>/<chantier>/<id>-<nom>` dans le bucket `chantier-documents`.
 * L'identifiant rend le chemin unique ; le nom, débarrassé de ce qu'un
 * chemin ne supporte pas, le garde lisible dans le Storage.
 */
export function siteDocumentPath(
  tenantId: string,
  projectId: string,
  id: string,
  fileName: string,
): string {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "") : "";
  const safe =
    stem
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^A-Za-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "document";
  return `${tenantId}/${projectId}/${id}-${safe}${ext ? `.${ext}` : ""}`;
}

/** Qui dépose et partage. */
export function canManageSiteDocuments(role: Role | undefined): boolean {
  return role === "patron" || role === "conducteur";
}

/** Qui retire. */
export function canRemoveSiteDocuments(role: Role | undefined): boolean {
  return role === "patron";
}

const KIND_ORDER: Record<SiteDocumentKind, number> = { plan: 0, cahier_des_charges: 1, autre: 2 };

/** Les plans d'abord, puis les cahiers des charges ; le plus récent en tête de chaque groupe. */
export function sortSiteDocuments(documents: readonly SiteDocument[]): SiteDocument[] {
  return [...documents].sort(
    (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || b.createdAt.localeCompare(a.createdAt),
  );
}

/** « 2,4 Mo », « 180 Ko ». */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} Mo`;
  return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
}
